import {useEffect, useRef, useState} from 'react';
import {notification} from 'antd';
import {Level, Package, Test} from '../../../graphql/API';
import {StudentsConnection} from '../../../graphql/API';
import {StudentsService} from '../../Students';
import {PackagesService} from '../../Package';
import {LevelsService} from '../../Level';
import {PlacementTestsService} from '../../PlacementTest/PlacementTestsService';
import {CurriculumsService} from '../../Curriculums/CurriculumsService';

const studentsService = new StudentsService();
const packagesService = new PackagesService();
const levelService = new LevelsService();
const placementTestsService = new PlacementTestsService();
const curriculumsService = new CurriculumsService();

/**
 * Shared hook — loads all dropdown data needed by both
 * EnrollmentCreate and EnrollmentEdit.
 */
export const useEnrollmentData = (withStudents = false) => {
  const [students, setStudents] = useState<StudentsConnection>();
  const [packages, setPackages] = useState<Package[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [curriculums, setCurriculums] = useState<any[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  const searchTimeout = useRef<any>();

  useEffect(() => {
    const promises: Promise<any>[] = [
      curriculumsService.getCurriculums(),
      levelService.getLevels(),
      packagesService.getPackages(1, 100),
      placementTestsService.getPlacementTests(1, 100),
    ];

    if (withStudents) {
      setIsLoadingStudents(true);
      promises.push(studentsService.getStudents(null));
    }

    Promise.allSettled(promises)
      .then(([currs, lvls, pkgs, tstsResult, stds]) => {
        if (currs.status === 'fulfilled') setCurriculums(currs.value || []);
        if (lvls.status === 'fulfilled') setLevels(lvls.value);
        if (pkgs.status === 'fulfilled') {
          const onlyAI = (pkgs.value.items as any[]).filter(p => p.onlyAI);
          setPackages(onlyAI as any);
        }
        if (tstsResult.status === 'fulfilled') setTests(tstsResult.value.items);
        if (withStudents && stds && stds.status === 'fulfilled') {
          setStudents(stds.value);
          setIsLoadingStudents(false);
        }
      })
      .catch(() => {
        notification.error({message: 'Failed to load form data'});
      });
  }, []);

  const searchStudents = (
    nextToken: string | null,
    searchBy: 'NAME' | 'ID',
    textSearch?: string,
  ) => {
    setIsLoadingStudents(true);
    studentsService
      .getStudents(nextToken, searchBy, textSearch)
      .then(stds => {
        setStudents(stds);
        setIsLoadingStudents(false);
      })
      .catch(() => setIsLoadingStudents(false));
  };

  const onStudentSearch = (value: string, searchBy: 'NAME' | 'ID') => {
    setIsLoadingStudents(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(
      () => searchStudents(null, searchBy, value),
      600,
    );
  };

  const onPopupScroll = (event: any, searchBy: 'NAME' | 'ID') => {
    const target = event.target;
    if (
      !isLoadingStudents &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      setIsLoadingStudents(true);
      studentsService
        .getStudents(students?.nextToken ?? null)
        .then(stds => {
          setStudents(prev => ({
            ...prev,
            ...stds,
            items: [...(prev?.items ?? []), ...stds.items],
          }));
          setIsLoadingStudents(false);
        })
        .catch(() => setIsLoadingStudents(false));
    }
  };

  const onTestSearch = (value: string) => {
    if (value.length > 3) {
      placementTestsService
        .searchPlacementTestsByName(value)
        .then(r => setTests(r.items));
    }
  };

  return {
    students,
    packages,
    levels,
    tests,
    curriculums,
    isLoadingStudents,
    searchStudents,
    onStudentSearch,
    onPopupScroll,
    onTestSearch,
  };
};
